import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  placement?: TooltipPlacement;
  /** popup 弹层自定义 className */
  className?: string;
  /** 外层 span wrapper 自定义 className，默认 'inline-flex' */
  wrapperClassName?: string;
}

/**
 * 轻量 Tooltip，hover 400ms 后在 body 层渲染提示内容。
 * 支持 string 和 ReactNode（多行富文本），自动判断上方空间不足时翻转到下方。
 * 使用 Portal 避免被父容器 overflow hidden 裁剪。
 */
export function Tooltip({ content, children, placement = 'top', className, wrapperClassName }: TooltipProps) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number; placement: TooltipPlacement }>({ x: 0, y: 0, placement });
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const ref = useRef<HTMLSpanElement>(null);

  const onEnter = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        // 上方空间不足时把 top* 方位翻转到对应 bottom* 方位，避免提示被视口裁剪
        const effective: TooltipPlacement = placement.startsWith('top') && rect.top < 240
          ? (placement.replace('top', 'bottom') as TooltipPlacement)
          : placement;
        let x = 0;
        let y = 0;

        switch (effective) {
          case 'top':
            x = rect.left + rect.width / 2;
            y = rect.top - 6;
            break;
          case 'bottom':
            x = rect.left + rect.width / 2;
            y = rect.bottom + 6;
            break;
          case 'left':
            x = rect.left - 6;
            y = rect.top + rect.height / 2;
            break;
          case 'right':
            x = rect.right + 6;
            y = rect.top + rect.height / 2;
            break;
          case 'top-start':
            x = rect.left;
            y = rect.top - 6;
            break;
          case 'top-end':
            x = rect.right;
            y = rect.top - 6;
            break;
          case 'bottom-start':
            x = rect.left;
            y = rect.bottom + 6;
            break;
          case 'bottom-end':
            x = rect.right;
            y = rect.bottom + 6;
            break;
        }

        setPos({ x, y, placement: effective });
      }
      setShow(true);
    }, 400);
  }, [placement]);

  const onLeave = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setShow(false);
  }, []);

  // 根据位置计算 transform
  const getTransform = (p: TooltipPlacement) => {
    switch (p) {
      case 'top':
      case 'top-start':
      case 'top-end':
        return p === 'top' ? '-translate-x-1/2 -translate-y-full' : p === 'top-start' ? '-translate-y-full' : '-translate-x-full -translate-y-full';
      case 'bottom':
      case 'bottom-start':
      case 'bottom-end':
        return p === 'bottom' ? '-translate-x-1/2' : p === 'bottom-start' ? '' : '-translate-x-full';
      case 'left':
        return '-translate-x-full -translate-y-1/2';
      case 'right':
        return '-translate-y-1/2';
      default:
        return '-translate-x-1/2 -translate-y-full';
    }
  };

  return (
    <>
      <span ref={ref} className={wrapperClassName ?? 'inline-flex'} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        {children}
      </span>
      {show &&
        createPortal(
          <div
            className={cn(
              'fixed z-[9999] max-w-[360px] rounded-md border border-border/60 bg-card px-3 py-2 text-[11px] leading-relaxed text-foreground shadow-lg pointer-events-none whitespace-normal',
              getTransform(pos.placement),
              className,
            )}
            style={{ left: pos.x, top: pos.y }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
